import { OAuthCredential } from './OAuth.types';

enum OAuthSessionKey {
  State = 'OAuth.state.',
  CodeVerifier = 'OAuth.codeVerifier.',
  RedirectUri = 'OAuth.redirectUri.',
  RedirectUriSearch = 'OAuth.redirectUri.search',
}

export default class OAuth {
  private credentialKey: string;
  private scope: string;
  private authorizationUrl: string;
  private tokenUrl: string;
  private profileUrl?: string;
  private clientId: string;
  private extraParams?: Record<string, string | number>;

  constructor(params: OAuthCredential & { key: string }) {
    this.credentialKey = params.key;
    this.scope = params.scope;
    this.authorizationUrl = params.authorizationUrl;
    this.tokenUrl = params.tokenUrl;
    this.profileUrl = params.profileUrl;
    this.clientId = params.clientId;
    this.extraParams = params.extraParams;
  }

  private getSessionKey(key: string) {
    return key + this.credentialKey;
  }

  private setSessionRedirectUriSearch(search: string) {
    sessionStorage.setItem(OAuthSessionKey.RedirectUriSearch, search);
  }

  private getSessionRedirectUriSearch() {
    return sessionStorage.getItem(OAuthSessionKey.RedirectUriSearch);
  }

  private removeSessionRedirectUriSearch() {
    return sessionStorage.removeItem(OAuthSessionKey.RedirectUriSearch);
  }

  private setSessionState(state: string) {
    sessionStorage.setItem(this.getSessionKey(OAuthSessionKey.State), state);
  }

  private getSessionState() {
    return sessionStorage.getItem(this.getSessionKey(OAuthSessionKey.State));
  }

  private removeSessionState() {
    return sessionStorage.removeItem(this.getSessionKey(OAuthSessionKey.State));
  }

  private setSessionCodeVerifier(codeVerifier: string) {
    sessionStorage.setItem(
      this.getSessionKey(OAuthSessionKey.CodeVerifier),
      codeVerifier,
    );
  }

  private getSessionCodeVerifier() {
    return sessionStorage.getItem(
      this.getSessionKey(OAuthSessionKey.CodeVerifier),
    );
  }

  private removeSessionCodeVerifier() {
    return sessionStorage.removeItem(
      this.getSessionKey(OAuthSessionKey.CodeVerifier),
    );
  }

  private setSessionRedirectUri(redirectUri: string) {
    sessionStorage.setItem(
      this.getSessionKey(OAuthSessionKey.RedirectUri),
      redirectUri,
    );
  }

  private getSessionRedirectUri() {
    return sessionStorage.getItem(
      this.getSessionKey(OAuthSessionKey.RedirectUri),
    );
  }

  private removeSessionRedirectUri() {
    return sessionStorage.removeItem(
      this.getSessionKey(OAuthSessionKey.RedirectUri),
    );
  }

  private setCredentialTokens<T>(credentialKey: string, data: T) {
    localStorage.setItem(`OAuth.tokens.${credentialKey}`, JSON.stringify(data));
  }

  static getCredentialTokens<T>(credentialKey: string): T | undefined {
    const tokens = localStorage.getItem(`OAuth.tokens.${credentialKey}`);
    return tokens ? JSON.parse(tokens) : undefined;
  }

  private getRedirectUri(redirect_uri: string) {
    const url = new URL(redirect_uri);

    this.setSessionRedirectUriSearch(url.search.replace('?', ''));

    url.search = `${url.search}&credential_key=${this.credentialKey}`;

    return url.toString();
  }

  async signInRedirect(redirect_uri: string) {
    const state = this.generateState();
    const codeVerifier = this.generateCodeVerifier();
    const code_challenge = await this.generateCodeChallenge(codeVerifier);

    const redirectUri = this.getRedirectUri(redirect_uri);

    this.setSessionState(state);
    this.setSessionCodeVerifier(codeVerifier);
    this.setSessionRedirectUri(redirectUri);

    const search = new URLSearchParams({
      response_type: 'code',
      client_id: this.clientId,
      redirect_uri: redirectUri,
      state,
      code_challenge,
      code_challenge_method: 'S256',
      ...this.extraParams,
    });

    if (this.scope) {
      search.append('scope', this.scope);
    }

    window.location.href = `${this.authorizationUrl}?${search}`;
  }

  retriveCode() {
    return new Promise<{
      code?: string;
      scope?: string;
      error?: string;
    }>((res, rej) => {
      const search = new URLSearchParams(window.location.search);
      const params = Object.fromEntries(search);
      const state = this.getSessionState();

      if (params.state && !params.credential_key) {
        return rej(new Error('Invalid credential key'));
      }

      if (params.state && params.error) {
        return rej(new Error(params.error));
      }

      if (params.state && params.state !== state) {
        return rej(new Error('Invalid state code'));
      }

      if (params.code) {
        res(params);

        const url = new URL(window.location as unknown as URL);
        url.search = this.getSessionRedirectUriSearch() || '';

        this.removeSessionRedirectUriSearch();

        history.replaceState(null, '', url.toString());

        return;
      }
    });
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  retriveTokens<T = any>(code: string): Promise<T> {
    return new Promise<T>(async (res, rej) => {
      const redirect_uri = this.getSessionRedirectUri();
      const code_verifier = this.getSessionCodeVerifier();

      if (!redirect_uri || !code_verifier) return rej(Error('Invalid Params'));

      const body = new URLSearchParams({
        grant_type: 'authorization_code',
        code,
        redirect_uri,
        client_id: this.clientId,
        code_verifier,
      });

      await fetch(`${this.tokenUrl}`, {
        method: 'POST',
        headers: new Headers({
          'Content-type': 'application/x-www-form-urlencoded;charset=UTF-8',
        }),
        body,
      })
        .then(async (data) => {
          const result = await data.json();
          this.setCredentialTokens(this.credentialKey, result);
          res(result);
        })
        .catch(rej);

      this.clearSession();
    });
  }

  private generateState() {
    const array = new Uint8Array(16);
    window.crypto.getRandomValues(array);
    return Array.from(array, (byte) => byte.toString(16).padStart(2, '0')).join(
      '',
    );
  }

  private generateCodeVerifier() {
    const array = new Uint8Array(32);
    window.crypto.getRandomValues(array);
    const base64UrlSafe = btoa(
      String.fromCharCode.apply(null, array as unknown as number[]),
    )
      .replace(/\+/g, '-')
      .replace(/\//g, '_')
      .replace(/=+$/, '');

    return base64UrlSafe;
  }

  private async generateCodeChallenge(codeVerifier: string) {
    const encoder = new TextEncoder();
    const data = encoder.encode(codeVerifier);

    const hash = await crypto.subtle.digest('SHA-256', data);
    const base64UrlSafe = btoa(
      String.fromCharCode.apply(
        null,
        new Uint8Array(hash) as unknown as number[],
      ),
    )
      .replace(/\+/g, '-')
      .replace(/\//g, '_')
      .replace(/=+$/, '');

    return base64UrlSafe;
  }

  private clearSession() {
    this.removeSessionState();
    this.removeSessionCodeVerifier();
    this.removeSessionRedirectUri();
  }
}
